/**
 * ServiceOrderPanel — the order-driven service picker used by every
 * "New Request" surface in NTN Verify Hub.
 *
 * The order placed here becomes the blueprint for the entire application
 * lifecycle: workflow tracks, staff queues, verification-findings display,
 * and the recommendation-ready gate are all derived from ``services``.
 * See `/app/backend/routes/workflow.py::_progress_milestones` and
 * `/app/backend/routes/queue.py::_section_was_ordered`.
 *
 * Structure:
 *   ⭐ Decision-Ready Screening Package  (featured, recommended bundle)
 *   ── Screening Intelligence            (DecisionPoint / Criminal / SSN)
 *   ── Verification Services             (Employment / Landlord / Documents)
 *
 * The bundle is a UI-only convenience: clicking it selects all six
 * individual services AND sets ``pkg = "decision_ready"`` so the backend
 * can later report on bundled orders (see ScreeningCreate.package).
 */
import {
  INTELLIGENCE_SERVICES,
  VERIFICATION_SERVICES,
  DECISION_READY_PACKAGE,
} from "@/lib/api";
import { Check, Sparkles } from "lucide-react";

const isFullScope = (services) =>
  services.length === DECISION_READY_PACKAGE.length &&
  DECISION_READY_PACKAGE.every((k) => services.includes(k));

export default function ServiceOrderPanel({ services, onChange, pkg, onPackageChange, testidPrefix = "svc", allowedProducts }) {
  // Phase E.2 (2026-02): the company's allowlist controls which products
  // even appear. If `allowedProducts` is undefined (staff-side callers)
  // every product is shown — the backend still runs its own guard.
  const isAllowed = (key) => !allowedProducts || allowedProducts.includes(key);
  const allowedBundle = DECISION_READY_PACKAGE.filter(isAllowed);
  const intelItems = INTELLIGENCE_SERVICES.filter((r) => isAllowed(r.key));
  const verifItems = VERIFICATION_SERVICES.filter((r) => isAllowed(r.key));
  const bundleActive = pkg === "decision_ready" && isFullScope(services) && allowedBundle.length === DECISION_READY_PACKAGE.length;

  const selectBundle = () => {
    onChange([...allowedBundle]);
    if (allowedBundle.length === DECISION_READY_PACKAGE.length) {
      onPackageChange?.("decision_ready");
    }
  };

  const toggleService = (key) => {
    const next = services.includes(key)
      ? services.filter((s) => s !== key)
      : [...services, key];
    onChange(next);
    // Any manual toggle away from full-scope clears the bundle marker so
    // the "package" attribution reflects reality.
    if (pkg === "decision_ready" && !isFullScope(next)) {
      onPackageChange?.(null);
    }
    if (pkg !== "decision_ready" && isFullScope(next)) {
      // Silently opt into the marker when the user hand-picks every item.
      onPackageChange?.("decision_ready");
    }
  };

  return (
    <div className="space-y-5" data-testid={`${testidPrefix}-order-panel`}>
      {/* Featured bundle — hidden entirely if the company can't order the full package */}
      {allowedBundle.length === DECISION_READY_PACKAGE.length && (
      <button
        type="button"
        onClick={selectBundle}
        data-testid={`${testidPrefix}-bundle-decision-ready`}
        className={`w-full text-left rounded-lg border-2 p-5 transition-all ${
          bundleActive
            ? "border-amber-500 bg-gradient-to-br from-amber-50/70 to-white ring-2 ring-amber-300 shadow-md"
            : "border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50/40"
        }`}
      >
        <div className="flex items-start gap-3">
          <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${
            bundleActive ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-700"
          }`}>
            {bundleActive ? <Check className="h-5 w-5" strokeWidth={2.5} /> : <Sparkles className="h-4 w-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-amber-700">Recommended</span>
              <span className="text-[10px] font-mono tracking-wider uppercase text-slate-400">6 services</span>
            </div>
            <div className="font-serif text-lg text-slate-900 mt-1 leading-tight">
              ⭐ Decision-Ready Screening Package
            </div>
            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
              The complete workflow for a Decision-Ready Application. Runs every intelligence report and every verification service so NTN can post a confident recommendation without follow-up.
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {DECISION_READY_PACKAGE.map((k) => (
                <span key={k} className={`text-[10px] font-mono tracking-wider uppercase px-2 py-0.5 rounded-full border ${
                  bundleActive ? "border-amber-300 bg-amber-100 text-amber-800" : "border-slate-200 bg-slate-50 text-slate-600"
                }`}>
                  {INTELLIGENCE_SERVICES.concat(VERIFICATION_SERVICES).find((r) => r.key === k)?.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </button>
      )}

      {/* Individual sections — only the products actually allowed */}
      {intelItems.length > 0 && (
      <Section
        title="Screening Intelligence"
        eyebrow="Reports"
        items={intelItems}
        services={services}
        onToggle={toggleService}
        testidPrefix={`${testidPrefix}-intel`}
      />
      )}
      {verifItems.length > 0 && (
      <Section
        title="Verification Services"
        eyebrow="Human Verification"
        items={verifItems}
        services={services}
        onToggle={toggleService}
        testidPrefix={`${testidPrefix}-verif`}
      />
      )}
      {intelItems.length === 0 && verifItems.length === 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50/50 p-4 text-sm text-amber-900" data-testid={`${testidPrefix}-empty-catalog`}>
          Your account isn&apos;t authorized to order any products. Please contact NTN support to activate your product catalog.
        </div>
      )}
    </div>
  );
}

function Section({ title, eyebrow, items, services, onToggle, testidPrefix }) {
  return (
    <div>
      <div className="mb-2.5 flex items-center gap-2">
        <div className="h-1.5 w-1.5 rounded-full bg-slate-400" />
        <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500">{eyebrow}</span>
        <span className="text-sm font-serif text-slate-900">{title}</span>
      </div>
      <div className="grid sm:grid-cols-3 gap-2.5" data-testid={`${testidPrefix}-list`}>
        {items.map((r) => {
          const on = services.includes(r.key);
          return (
            <button
              type="button"
              key={r.key}
              onClick={() => onToggle(r.key)}
              data-testid={`${testidPrefix}-${r.key}`}
              className={`text-left rounded-md border p-3 transition-all ${
                on
                  ? "border-slate-900 bg-slate-900 text-white ring-1 ring-slate-900"
                  : "border-slate-200 bg-white hover:border-slate-400 text-slate-900"
              }`}
            >
              <div className="flex items-center gap-2">
                <div className={`h-4 w-4 rounded-sm flex items-center justify-center shrink-0 ${
                  on ? "bg-white text-slate-900" : "border border-slate-300"
                }`}>
                  {on && <Check className="h-3 w-3" strokeWidth={3} />}
                </div>
                <span className="font-semibold text-sm">{r.label}</span>
              </div>
              <p className={`text-[11px] mt-1.5 leading-snug ${on ? "text-white/70" : "text-slate-500"}`}>
                {r.desc}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
